import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '~/lib/api';

export default function TbitInvoicesUploadPage() {
  const [file, setFile] = useState<File | null>(null);
  const [vendorName, setVendorName] = useState('');
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const acceptedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];

  const handleFile = useCallback((f: File) => {
    if (!acceptedTypes.includes(f.type)) {
      setError('Solo se aceptan archivos PDF e imágenes (PNG, JPG).');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setError('El archivo debe pesar menos de 10 MB.');
      return;
    }
    setError('');
    setFile(f);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }, [handleFile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (vendorName.trim()) formData.append('vendor_name', vendorName.trim());
      if (notes.trim()) formData.append('notes', notes.trim());

      const res = await apiFetch('/tbit-invoices/api/records/upload', {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || `Error al subir (${res.status})`);
      }

      const data = await res.json();
      navigate(`/tbit-invoices/records/${data.id}`);
    } catch (err: any) {
      setError(err.message || 'Error al subir el archivo');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-2">Subir factura</h1>
      <p className="text-sm text-base-content/60 mb-6">
        Usa esta pantalla para facturas que no llegaron por correo o que necesitas cargar de forma puntual. Nosotros leemos el documento y lo registramos en NetSuite.
      </p>

      <form onSubmit={handleSubmit} className="card bg-base-100 shadow">
        <div className="card-body space-y-5">
          {/* File dropzone */}
          <div
            className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${
              dragOver ? 'border-primary bg-primary/5' : file ? 'border-success bg-success/5' : 'border-base-300'
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            {file ? (
              <div className="space-y-2">
                <p className="text-success font-semibold">{file.name}</p>
                <p className="text-sm text-base-content/50">
                  {(file.size / 1024).toFixed(0)} KB &middot; {file.type}
                </p>
                <button
                  type="button"
                  className="btn btn-ghost btn-xs"
                  onClick={(e) => { e.stopPropagation(); setFile(null); }}
                >
                  Quitar
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-base-content/60">
                  Arrastra un PDF o imagen aquí, o haz clic para buscar
                </p>
                <p className="text-xs text-base-content/40">Máx 10 MB &middot; PDF, PNG, JPG</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
            />
          </div>

          {/* Vendor name */}
          <div className="form-control w-full">
            <label className="label">
              <span className="label-text">Nombre del proveedor (opcional)</span>
            </label>
            <input
              type="text"
              className="input input-bordered w-full"
              placeholder="Ej: Suministros Alpha S.A.S."
              value={vendorName}
              onChange={(e) => setVendorName(e.target.value)}
            />
          </div>

          {/* Notes */}
          <div className="form-control w-full">
            <label className="label">
              <span className="label-text">Información para clasificar (opcional)</span>
            </label>
            <textarea
              className="textarea textarea-bordered w-full"
              placeholder={`Ej:\n• "Gasto generado por Juan, Jefe de Marketing"\n• "Departamento: Logística — Categoría: Combustibles"\n• "Cuenta contable: 51953501 Combustibles y Lubricantes"`}
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <p className="text-xs text-base-content/50 mt-1">
              Indica quién generó el gasto, el departamento o categoría, o cualquier otra pista que ayude a clasificarlo. La IA lo tendrá en cuenta junto a tus{' '}
              <a href="/tbit-invoices/memorias" className="link link-primary">reglas contables</a>.
            </p>
          </div>

          {error && (
            <div className="alert alert-error">
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary w-full"
            disabled={!file || uploading}
          >
            {uploading ? (
              <><span className="loading loading-spinner loading-sm" /> Procesando...</>
            ) : (
              'Subir y procesar'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
